import dayjs from 'dayjs'
import { PMPBodySectionItems } from './styledComponent'
import { getParseDataFromHtmlString } from 'src/consumer/helpers/utilFunctions'
import { PRESCRIPTION_COLORS_MAP } from 'src/mentor/components/PrescriptionMakingPage/inputComponents/common/color-utils'

export default function PrintablePatientHistoryNotes({
  patientHistoryNotes,
  debug,
  debugHighlightStates,
  prescriptionViewSettings,
  patientHistoryNotesRef,
  styleOverrides,
})
 {
  return (
    <PMPBodySectionItems
      ref={patientHistoryNotesRef}
      debug={debug}
      debugColor={PRESCRIPTION_COLORS_MAP.patientHistoryNotes?.color}
      borderLeft={styleOverrides?.borderLeft}
      paddingLeft={styleOverrides?.paddingLeft}
    >
      <h2 style={{ 
        textDecoration: 'underline', 
        display: 'block', 
        marginBottom: '1em',
        marginTop: 0,
        fontSize: '18px',
        fontWeight: 700,
        color: '#1e293b',
        pageBreakAfter: 'avoid',
      }}>
        Patient History Notes
      </h2>
      
      {patientHistoryNotes?.map((note, index) => {
        const parsedContent = getParseDataFromHtmlString(note?.content?.content)
        
        return (
          <div 
            key={note?.id || `note-${index}`}
            className="patient-history-note-item"
            style={{
              marginBottom: '1.5em',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <strong style={{ display: 'block', marginBottom: '0.5em', color: '#4338ca' }}>
              {dayjs.utc(note?.createdTime).format('DD MMMM YYYY')}
            </strong>
            <div 
              className="tiptap-content"
              style={{ 
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: '1.6',
                fontSize: '13px',
              }}
            >
              {parsedContent}
            </div>
          </div>
        )
      })}
      
      
      {/* Inline CSS for TipTap content - optimized for long rich text with page breaks */}
      <style>{`
        /* Container for patient history notes - allow free-flowing across pages */
        .patient-history-notes-container {
          page-break-inside: auto !important;
          break-inside: auto !important;
        }
        
        .tiptap-content {
          max-width: 100%;
          overflow: visible;
          page-break-inside: auto;
          break-inside: auto;
        }
        
        .tiptap-content p {
          margin: 0 0 0.8em 0;
          line-height: 1.6;
          text-align: left;
          word-spacing: normal;
          hyphens: auto;
          page-break-inside: avoid;
          break-inside: avoid;
          orphans: 3;
          widows: 3;
        }
        
        .tiptap-content p:last-child {
          margin-bottom: 0;
        }
        
        .tiptap-content p:empty {
          display: none;
        }
        
        .tiptap-content ul,
        .tiptap-content ol {
          margin: 0.8em 0;
          padding-left: 1.5em;
          line-height: 1.6;
          page-break-inside: auto;
        }
        
        .tiptap-content li {
          margin-bottom: 0.4em;
          text-align: left;
          hyphens: auto;
          page-break-inside: auto;
        }
        
        .tiptap-content li:last-child {
          margin-bottom: 0;
        }
        
        .tiptap-content strong,
        .tiptap-content b {
          font-weight: 600;
        }
        
        .tiptap-content em,
        .tiptap-content i {
          font-style: italic;
        }
        
        .tiptap-content u {
          text-decoration: underline;
        }
        
        .tiptap-content h1,
        .tiptap-content h2,
        .tiptap-content h3,
        .tiptap-content h4,
        .tiptap-content h5,
        .tiptap-content h6 {
          margin: 1em 0 0.5em 0;
          line-height: 1.3;
          font-weight: 600;
          page-break-after: avoid;
        }
        
        .tiptap-content h1:first-child,
        .tiptap-content h2:first-child,
        .tiptap-content h3:first-child {
          margin-top: 0.3em;
        }
        
        .tiptap-content h1 { font-size: 1.3em; }
        .tiptap-content h2 { font-size: 1.2em; }
        .tiptap-content h3 { font-size: 1.1em; }
        .tiptap-content h4 { font-size: 1.05em; }
        .tiptap-content h5 { font-size: 1em; }
        .tiptap-content h6 { font-size: 0.95em; }
        
        .tiptap-content blockquote {
          margin: 0.8em 0;
          padding-left: 1em;
          border-left: 3px solid #ccc;
          font-style: italic;
          color: #555;
        }
        
        .tiptap-content code {
          background-color: #f4f4f4;
          padding: 0.1em 0.3em;
          border-radius: 3px;
          font-family: 'Courier New', monospace;
          font-size: 0.9em;
        }
        
        .tiptap-content pre {
          background-color: #f4f4f4;
          padding: 0.5em;
          border-radius: 4px;
          overflow-x: auto;
          margin: 0.8em 0;
        }
        
        .tiptap-content pre code {
          background: none;
          padding: 0;
        }
        
        /* Allow content to flow naturally - no height restrictions */
        .patient-history-note-item {
          max-width: 100%;
          overflow: visible;
          page-break-inside: avoid; /* Try to keep each note item together */
          break-inside: avoid;
          orphans: 2;
          widows: 2;
        }
        
        /* Allow page breaks between note items if needed */
        .patient-history-note-item + .patient-history-note-item {
          page-break-before: auto;
          break-before: auto;
          margin-top: 1.5em;
        }
        
        /* Ensure content can flow across pages in print */
        @media print {
          .patient-history-notes-container {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            page-break-inside: auto;
            break-inside: auto;
          }
          
          .patient-history-note-item {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* If a note is too long, allow it to break but try to keep paragraphs together */
          .patient-history-note-item .tiptap-content {
            page-break-inside: auto;
            break-inside: auto;
          }
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .tiptap-content {
            font-size: 12px;
          }
          
          .tiptap-content p {
            margin-bottom: 0.6em;
          }
          
          .tiptap-content ul,
          .tiptap-content ol {
            padding-left: 1.2em;
            margin: 0.6em 0;
          }
          
          .tiptap-content li {
            margin-bottom: 0.3em;
          }
        }
      `}</style>
    </PMPBodySectionItems>
  )
}
